import { useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Pencil, Swords } from 'lucide-react'
import { toast } from 'sonner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import { updateMe } from '@/api/auth'
import { useAuth } from '@/context/AuthContext'
import {
  useFriendOfFriends,
  useFriendProfile,
  useInviteProfile,
  useUnfriend,
} from '@/hooks/useFriends'
import type { Relation } from '@/api/friends'
import type { OriginalTrophy } from '@/api/trophies'
import FriendAvatar from '@/components/feature/friends/FriendAvatar'
import FriendRow from '@/components/feature/friends/FriendRow'
import HandleCopy from '@/components/feature/friends/HandleCopy'
import RequestButton from '@/components/feature/friends/RequestButton'
import { StaticShelf } from '@/components/feature/trophy/trophyShared'

const PAGE_SIZE = 10

export default function FriendProfilePage() {
  const { id, token } = useParams()
  const inviteMode = Boolean(token)
  const userId = Number(id)

  // 招待モードはトークン経由、通常モードは数値IDで取得（両フックを常に呼ぶ＝Hooks 規約）
  const normal = useFriendProfile(inviteMode ? NaN : userId)
  const invite = useInviteProfile(token ?? '')
  const { data, isLoading, isError } = inviteMode ? invite : normal

  // 通常モードで id が数値でない（ルーティング設定ミス等）場合はフレンド一覧へ退避
  if (!inviteMode && Number.isNaN(userId)) {
    return <Navigate to="/friends" replace />
  }

  if (isLoading) {
    return <p className="py-10 text-center text-sm text-muted-foreground">読み込み中…</p>
  }
  if (isError || !data) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        このユーザーのプロフィールは表示できません。
      </p>
    )
  }

  const { user, luck, relation, request_relation, shelf } = data
  // 招待モードでは数値IDが無いため、対象IDはレスポンスの user.id を使う。
  const targetId = user.id

  return (
    <div className="flex flex-col gap-6 py-4">
      {/* ヘッダー */}
      <div className="flex flex-col items-center gap-2">
        <FriendAvatar nickname={user.nickname} avatarUrl={user.avatar_url} size={80} />
        <div className="flex items-center gap-1.5">
          <p className="text-lg font-bold">{user.nickname}</p>
          {relation === 'self' && (
            <EditNicknameButton current={user.nickname} userId={targetId} />
          )}
        </div>
        <HandleCopy handle={user.handle} />
        <div className="flex items-center gap-3 text-sm tabular-nums">
          <span className="flex items-center gap-1">
            <Swords size={16} className="text-muted-foreground" />
            戦闘力 {user.power.toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            🍀運 {luck.toLocaleString()}
          </span>
        </div>
        <ProfileAction userId={targetId} relation={relation} requestRelation={request_relation} />
      </div>

      {/* 自由棚 */}
      {shelf.state !== 'hidden' && (
        <section>
          <h2 className="mb-2 text-sm font-medium">自由棚</h2>
          {shelf.state === 'private' ? (
            <p className="py-6 text-center text-sm text-muted-foreground">非公開</p>
          ) : (
            <Shelf trophies={shelf.trophies ?? []} />
          )}
        </section>
      )}

      {/* フレンドのフレンド */}
      <FriendOfFriendsSection userId={targetId} />
    </div>
  )
}

function ProfileAction({
  userId, relation, requestRelation,
}: {
  userId: number
  relation: 'self' | 'friend' | 'fof' | 'none'
  requestRelation: Relation
}) {
  const navigate = useNavigate()
  const unfriend = useUnfriend()
  const [confirmOpen, setConfirmOpen] = useState(false)

  if (relation === 'self') return null

  if (relation === 'friend') {
    return (
      <>
        <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
          フレンド解除
        </Button>
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="フレンドを解除しますか？"
          description="解除すると相手のプロフィールが見られなくなります。"
          confirmLabel="解除する"
          variant="destructive"
          isPending={unfriend.isPending}
          onConfirm={() =>
            unfriend.mutate(userId, {
              onSuccess: () => {
                toast.success('フレンドを解除しました')
                navigate('/friends')
              },
            })
          }
        />
      </>
    )
  }

  // fof / none（招待リンク経由）：申請状態（pending 含む）を RequestButton に
  // 委譲し、重複申請を防ぐ
  return <RequestButton userId={userId} relation={requestRelation} />
}

function EditNicknameButton({ current, userId }: { current: string; userId: number }) {
  const { refreshUser } = useAuth()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(current)

  const mutation = useMutation({
    mutationFn: (nickname: string) => updateMe(nickname),
    onSuccess: async () => {
      await refreshUser()
      qc.invalidateQueries({ queryKey: ['friends', 'profile', userId] })
      toast.success('ニックネームを変更しました')
      setOpen(false)
    },
  })

  const openDialog = () => {
    setValue(current)
    setOpen(true)
  }

  const trimmed = value.trim()

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="text-muted-foreground hover:text-foreground"
        aria-label="ニックネームを編集"
      >
        <Pencil size={16} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ニックネームを変更</DialogTitle>
          </DialogHeader>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={50}
            placeholder="ニックネーム"
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              キャンセル
            </Button>
            <Button
              disabled={!trimmed || trimmed === current || mutation.isPending}
              onClick={() => mutation.mutate(trimmed)}
            >
              保存
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Shelf({ trophies }: { trophies: OriginalTrophy[] }) {
  if (trophies.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">まだ何も飾られていません。</p>
  }
  const sorted = [...trophies].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  return <StaticShelf items={sorted} onTap={() => {}} />
}

function FriendOfFriendsSection({ userId }: { userId: number }) {
  const navigate = useNavigate()
  const [offset, setOffset] = useState(0)
  const { data } = useFriendOfFriends(userId, offset, PAGE_SIZE)
  const items = data?.results ?? []
  const total = data?.count ?? 0
  const hasMore = offset + PAGE_SIZE < total

  if (total === 0 && items.length === 0) return null

  return (
    <section>
      <h2 className="mb-2 text-sm font-medium">フレンドのフレンド</h2>
      <div className="divide-y">
        {items.map((u) => (
          <FriendRow
            key={u.id}
            nickname={u.nickname}
            handle={u.handle}
            avatarUrl={u.avatar_url}
            power={u.power}
            onClick={() => navigate(`/friends/${u.id}`)}
            actions={<RequestButton userId={u.id} relation={u.relation} />}
          />
        ))}
      </div>
      {hasMore && (
        <Button variant="ghost" className="mt-2" onClick={() => setOffset(offset + PAGE_SIZE)}>
          もっと見る
        </Button>
      )}
    </section>
  )
}
